import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useIsNewUser } from '@confluence/targeting/entry-points/useIsNewUser.ts';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { ONBOARDING_EXPERIENCES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { differenceInDays } from 'date-fns';

/**
 * Hook to determine eligibility for auto-opening notifications experiment
 * Checks:
 * 1. User is logged in
 * 2. User is licensed
 * 3. User is new (less than 28 days)
 *
 * Returns false while user is not logged in or unlicensed
 */
export const useAutoOpenNotificationsEligibility = (): boolean => {
	const { isLoggedIn, accessStatus } = useSessionData();
	const isLicensed =
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.LICENSED_USE_ACCESS;

	const { isNewUser, loading: isNewUserLoading } = useIsNewUser({ activeDaysThreshold: 28 });

	const { data: onboardingStateData, loading: isOnboardingStateLoading } = useGetOnboardingState(
		[ONBOARDING_EXPERIENCES.NTH_USER_ONBOARDING_COMPLETED_TIMESTAMP],
		!isNewUserLoading && typeof isNewUser === 'boolean',
	);
	const { nthUserOnboardingCompletedTimestamp } = deserializeState(onboardingStateData);

	// newUser trait can have a latency leading to an indeterminate value,
	// as a fallback we check if the user onboarded within the last 28 days based on the onboarding completed timestamp
	// founding admin can be ignored here since by the time they receive a notification traits service will resolve

	const completedTimestamp = Number(nthUserOnboardingCompletedTimestamp);
	const isUserOnboardedRecently =
		Boolean(completedTimestamp) && differenceInDays(new Date(), new Date(completedTimestamp)) <= 28;

	if (!isLoggedIn || !isLicensed || isNewUserLoading || isOnboardingStateLoading) {
		return false;
	}

	if (typeof isNewUser === 'boolean') {
		return isNewUser;
	}

	return isUserOnboardedRecently;
};
